import { createFileRoute, Link } from '@tanstack/react-router'
import { GuideArticle, GuideSection } from '~/components/guide/GuideArticle'

export const Route = createFileRoute('/guide/eta')({
  component: EtaGuide,
})

function EtaGuide() {
  return (
    <GuideArticle>
      <GuideSection title="ETA Tracker">
        <p>
          Where <Link to="/" className="underline underline-offset-2">dispatch</Link>{' '}
          finds an officer to send, the{' '}
          <Link to="/eta" className="underline underline-offset-2">ETA Tracker</Link>{' '}
          follows the officer <em>already allocated</em> to a job. Enter a{' '}
          <strong>job number</strong> and press <strong>Track</strong>. It
          resolves the job&apos;s allocated officer (its executor), reads their
          recent location history, and shows how far away they are and whether
          they genuinely look en route.
        </p>
      </GuideSection>

      <GuideSection title="Linking to a job">
        <p>
          Open <code>/eta?job=J123455</code> to go straight to a job: the job
          number is filled in and tracking starts without pressing{' '}
          <strong>Track</strong>. The address bar updates whenever you track a
          job, so you can copy it to share, or build the same link into another
          tool such as an Argus dashboard. If you aren&apos;t signed in yet,
          you&apos;re returned to the same job after signing in.
        </p>
      </GuideSection>

      <GuideSection title="ETA and route">
        <p>
          The officer&apos;s current position is fed to the Google Routes API to
          get a live driving <strong>ETA</strong>, projected <strong>arrival
          time</strong> and <strong>road distance</strong> to site. Their recent
          coordinates are plotted as a route line on the map — from the{' '}
          <strong>route start</strong> (grey dot) to their position{' '}
          <strong>now</strong> (coloured chevron) — with the site shown as a
          black pin. The line colour matches the confidence verdict.
        </p>
      </GuideSection>

      <GuideSection title="En-route confidence">
        <p>
          A 0–100 <strong>confidence score</strong> summarises whether the
          officer looks genuinely on their way. It blends three signals, each
          shown as its own bar so the verdict is never a black box:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Heading to site</strong> — whether their direction of travel
            points toward the site (not just any movement).
          </li>
          <li>
            <strong>Consistent progress</strong> — the fraction of recent moves
            that reduced the distance to site.
          </li>
          <li>
            <strong>Data freshness</strong> — how recent their latest position
            is; stale data lowers confidence.
          </li>
        </ul>
        <p>
          The score doesn&apos;t depend on how far through the journey the
          officer is, so an officer who has just set off toward the site can
          already show <strong>On route</strong>.
        </p>
        <p>
          The score maps to a verdict: <strong>On route</strong>,{' '}
          <strong>Uncertain</strong> or <strong>Off route</strong>. Two special
          cases are called out separately — <strong>Stationary</strong> (barely
          moving, so direction can&apos;t be judged) and{' '}
          <strong>Insufficient data</strong> (too few recent positions to
          assess). Turn on <strong>Auto-refresh</strong> to re-poll every 30
          seconds while you watch a job.
        </p>
      </GuideSection>

      <GuideSection title="Kestrel comparison">
        <p>
          With <strong>Compare Kestrel on the ETA tracker</strong> switched on, the tracker also shows
          the same officer as Kestrel, the officer location service the Protect app reports to, sees
          them. The main ETA, route and confidence still come from EGIS; Kestrel is shown beside them
          so the two can be compared.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>On the map</strong>: Kestrel&apos;s route over the GPS age limit is the purple dashed line
            and its latest position the purple pin. If Google cannot work out its drive time, the card says{' '}
            <em>Drive time unavailable</em>; if Google finds no road route, <em>No drivable route found</em>. Readings Kestrel marks as mocked, clock-skewed or invalid are left out,
            and the card counts them as discarded.
          </li>
          <li>
            <strong>The Kestrel card</strong>: Kestrel&apos;s ETA, arrival and road distance, how accurate
            its latest reading was, how far it is from the EGIS position, its freshness as Kestrel classes it
            (Live, Allocatable, Stale, Silent or Tracking off) and its own en-route confidence. When Google
            finds no route from the Kestrel position, the card says so.
          </li>
          <li>
            <strong>Matched (unverified)</strong>: the officer was found by the Chase operative id the
            app reported, which Kestrel has not checked yet. <strong>Ambiguous match</strong>: more than
            one Kestrel officer claims that id, and the most recently seen is shown.
          </li>
        </ul>
        <p>When the card cannot be filled in it says why:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Kestrel unavailable</strong>: Kestrel could not be reached or refused Rapid Response&apos;s credentials.</li>
          <li><strong>No Kestrel officer</strong>: no officer heard from within the GPS age limit claims this operative.</li>
          <li><strong>No usable Kestrel readings</strong>: the officer is known, but nothing within the GPS age limit can be trusted as a position.</li>
        </ul>
        <p>
          Whether each source located the officer, and its confidence, is recorded for the board, so the two
          sources can be compared job by job: see <a href="#eta-comparison" className="underline underline-offset-2">Comparing EGIS and Kestrel</a>.
        </p>
      </GuideSection>

      <GuideSection title="Permissions">
        <p>
          The ETA Tracker reuses the dispatch read permission:{' '}
          <code>dispatch:read</code> is required to track a job. It is a live,
          read-only view — nothing is persisted.
        </p>
      </GuideSection>

      <GuideSection id="board-etas" title="Board ETAs for Argus">
        <p>
          Argus asks Rapid Response for the ETA of many jobs at once with{' '}
          <code>POST /api/v1/eta/jobs</code> (1 to 2,000 job numbers, requires{' '}
          <code>eta:read</code>). This is separate from the per-job page above:
          it keeps its own stored ETAs and the per-job page is unchanged.
        </p>
        <p>The rules for each job:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>A job checked in the last <strong>15 seconds</strong> is answered from storage without reading Chase.</li>
          <li>At most <strong>20</strong> Google calls are made per request, 4 at a time. Jobs with no ETA yet go before jobs whose ETA is being refreshed. Jobs beyond that keep their stored ETA until a later request; one with no ETA reads <em>Routing limit reached</em>.</li>
          <li>A Google call that takes longer than <strong>5 seconds</strong> counts as failed, and no call runs past <strong>15 seconds</strong> from the start of the request. A failed call keeps the stored ETA and the request still answers.</li>
          <li>A job gets no ETA, with a reason, when it is not found, has no location, has no officer, the officer is on site or has left site, the job is closed, there is no recent GPS, routing is unavailable, or the request&apos;s Google limits were reached before its turn.</li>
        </ul>
        <p>
          Each job&apos;s row carries the ETA and what it was worked out from:
          the Chase status, the officer and their partner, and when the officer
          was last seen. Any of these is empty (null) when Chase has no value.
          Other job details, such as the account, service or booking time, are
          not part of the row.
        </p>
        <p>
          With <strong>Compare Kestrel on the Argus board ETAs</strong> switched on, each row also carries a{' '}
          <code>kestrel</code> object: the same job worked out from Kestrel&apos;s position instead of Chase&apos;s,
          with the same move threshold, refresh time and GPS age limit. The EGIS columns are unchanged, and while the
          switch is off the object is left out of every row, including rows answered from storage.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Kestrel is read once per request, while the EGIS ETAs are worked out. If it does not answer within <strong>4 seconds</strong>, or by the end of the Kestrel lane, every row reads <em>Kestrel unavailable</em>; so does any other failure in the comparison, which never costs the EGIS columns.</li>
          <li>EGIS ETAs are worked out first. Kestrel then gets at most <strong>2 seconds</strong>, including any wait for its officer list, so the comparison adds no more than that to Argus&apos;s answer. No Kestrel ETA is started with less than half a second left; any not reached wait for a later request. EGIS counts only its own Google calls against the Google calls per minute, so it gets exactly the ETAs it would with Kestrel off. Kestrel only calls Google into room EGIS has left in the last minute, and at most <strong>half</strong> the Google calls per minute. In a minute where EGIS uses the whole limit after Kestrel has called, the total can reach one and a half times the limit.</li>
          <li>When Kestrel&apos;s position is within the move threshold of where the current EGIS ETA was worked out from, and that ETA is younger than the refresh time, the Kestrel ETA copies it instead of calling Google, and is marked <code>sharedWithEgis</code>.</li>
          <li>Its confidence is scored from Kestrel&apos;s own track and kept with its ETA. <em>No Kestrel officer</em> means no officer heard from within the GPS age limit claims the job&apos;s operative.</li>
          <li>Every ETA either source works out is recorded, for the comparison below.</li>
        </ul>
        <p>
          Four settings and two switches, edited under{' '}
          <Link to="/admin/eta-settings" className="underline underline-offset-2">Admin → ETA settings</Link>:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Move threshold</strong> (default 500 m): an officer who moves further than this gets a new ETA without waiting for the refresh time.</li>
          <li><strong>Refresh time</strong> (default 5 min): how old an ETA gets before it is worked out again, whether or not the officer has moved, so a stationary officer&apos;s ETA keeps up with traffic. A job whose last attempt gave no ETA is not tried again until this time has passed.</li>
          <li><strong>GPS age limit</strong> (default 30 min): with a latest fix older than this, a job with no ETA gets none, and an existing ETA is kept but its confidence reads insufficient data. Confidence only uses fixes within it.</li>
          <li><strong>Google calls per minute</strong> (default 60): EGIS&apos;s ceiling across all requests and servers. Kestrel&apos;s calls come out of what EGIS leaves.</li>
        </ul>
        <p>
          Two switches, both off by default, turn on the Kestrel comparison:
          Kestrel is the officer location service the Protect app reports to.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>Compare Kestrel on the ETA tracker</strong>: the tracker shows Kestrel&apos;s position, route and ETA beside EGIS&apos;s.</li>
          <li><strong>Compare Kestrel on the Argus board ETAs</strong>: board ETA rows carry a <code>kestrel</code> object, described above.</li>
        </ul>
        <p>
          The same page shows how many Google calls were made in the last hour,
          how many failed, and the last 24 hours, plus the board ETAs requested in the last hour.
          The page needs the <code>admin</code> permission.
        </p>
      </GuideSection>

      <GuideSection id="eta-comparison" title="Comparing EGIS and Kestrel">
        <p>
          <Link to="/analytics" search={{ tab: 'eta-sources' }} className="underline underline-offset-2">Analytics → ETA sources</Link>{' '}
          shows, for each job Argus asked about, which source located the officer between allocation and
          arrival: Chase (EGIS), Kestrel, both or neither. It needs the <code>analytics:read</code> permission.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>What is counted</strong>: each time the board works out a job while an allocated officer is on the way, it records each source&apos;s state and confidence, at most once a minute. Only the stretch from allocation (the job&apos;s first <em>Executing/Allocated</em> in EGIS) to arrival (its first <em>Executing/At Location</em>) counts. Jobs never allocated are left out. Recording started when this page was released, so older jobs are not listed.</li>
          <li><strong>Located</strong> means the source had a usable position for the officer within the GPS age limit, whether or not it had an ETA. <strong>Unavailable</strong> means Kestrel could not be read at all; it is not counted against Kestrel, and a job where Kestrel was only ever unavailable has its own filter. <strong>Not compared</strong> means Kestrel has no points for the job at all, because the board switch was off while the job was on the board; those jobs have their own filter too, and are left out of Kestrel&apos;s located share, Kestrel only, Chase only and Neither.</li>
          <li><strong>Headline</strong>: how many jobs each source located, Kestrel only, neither, and jobs with any Kestrel unavailability; each source&apos;s typical time to first ETA from allocation; its average confidence (per job first, over the times it had a position); and, on jobs where both had an ETA 15 minutes before arrival, which was closer.</li>
          <li><strong>Jobs</strong>: filter by Both, Chase only, Kestrel only, Neither or Kestrel unavailable. Each row shows a mark per source, a small confidence trend, and each source&apos;s time to first ETA. Click a job for its timeline: both sources&apos; confidence over time, the first ETA from each, and shaded stretches where Kestrel could not be read. Kestrel&apos;s line updates with each Kestrel ETA, so it moves in steps.</li>
        </ul>
        <p>
          The range covers the days Argus asked about the jobs, at most 31; the default is the last 7.
          Allocation and arrival are read from EGIS each time, so the page fails with an error while EGIS cannot be read.
        </p>
      </GuideSection>

      <GuideSection title="API">
        <p>
          The same data is available over REST for other services to consume:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <code>GET /api/v1/eta?jobNumber=…</code> — returns the allocated
            officer, their current location and travelled track, the driving ETA
            to site, and the confidence score with its factor breakdown, plus a{' '}
            <code>kestrel</code> section (null while the comparison is off)
            (requires <code>dispatch:read</code>).
          </li>
          <li>
            <code>POST /api/v1/eta/jobs</code> — board ETAs for a list of job
            numbers (requires <code>eta:read</code>).
          </li>
          <li>
            <code>GET</code> and <code>PUT /api/v1/eta/settings</code>,{' '}
            <code>GET /api/v1/eta/board-jobs</code> and{' '}
            <code>GET /api/v1/eta/calls</code> — the admin page&apos;s data (require{' '}
            <code>admin</code>).
          </li>
          <li>
            <code>GET /api/v1/eta/source-comparison?from=…&amp;to=…&amp;filter=…&amp;page=…</code> — the location coverage above,
            with the headline, the count per filter and a page of jobs (requires <code>analytics:read</code>).
          </li>
          <li>
            <code>GET /api/v1/eta/source-comparison/jobs/{'{jobNumber}'}</code> — one job&apos;s timeline: every point recorded
            for each source, its allocation, arrival and first ETAs (requires <code>analytics:read</code>).
          </li>
        </ul>
        <p>
          An MCP tool is generated automatically from this route. See the{' '}
          <Link to="/openapi" className="underline underline-offset-2">API reference</Link>{' '}
          and{' '}
          <Link to="/mcp-instructions" className="underline underline-offset-2">MCP instructions</Link>{' '}
          for details.
        </p>
      </GuideSection>
    </GuideArticle>
  )
}
